import { cn } from "@/lib/utils"

/**
 * The state glyphs (refined-system §2.3): every state has a shape as well as a colour. live = filled green circle (may
 * breathe), needs = filled cobalt circle (only for what waits on the viewer), blocked = red square, risk = amber
 * outline triangle, wait = hollow grey circle, done = ink check.
 */
export type GlyphState = "live" | "needs" | "blocked" | "risk" | "wait" | "done"

const glyphLabels: Record<GlyphState, string> = {
  live: "running",
  needs: "needs you",
  blocked: "blocked",
  risk: "at risk",
  wait: "waiting",
  done: "done",
}

export function StatusGlyph({
  state,
  size = 7,
  breathe = false,
  className,
  label,
}: {
  state: GlyphState
  /** The circle's size (6–10); the square is one smaller, the triangle 11 and the check 12. */
  size?: number | undefined
  breathe?: boolean | undefined
  className?: string | undefined
  label?: string | false | undefined
}) {
  const a11y =
    label === false
      ? ({ "aria-hidden": true } as const)
      : ({ role: "img", "aria-label": label ?? glyphLabels[state] } as const)
  if (state === "risk")
    return (
      <span className={cn("inline-flex shrink-0 text-risk", className)} {...a11y}>
        <svg
          width="11"
          height="11"
          viewBox="0 0 11 11"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5.5 1.5 10 9.5H1z" />
        </svg>
      </span>
    )
  if (state === "done")
    return (
      <span className={cn("inline-flex shrink-0 text-done", className)} {...a11y}>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M2.5 6.5 5 9l4.5-6" />
        </svg>
      </span>
    )
  const shape =
    state === "live"
      ? cn("rounded-full bg-live", breathe && "animate-breathe")
      : state === "needs"
        ? "rounded-full bg-needs"
        : state === "blocked"
          ? "bg-blocked"
          : "rounded-full border-[1.5px] border-wait bg-transparent"
  const px = state === "blocked" ? Math.max(5, size - 1) : size
  return (
    <span
      className={cn("inline-block shrink-0", shape, className)}
      style={{ width: px, height: px }}
      {...a11y}
    />
  )
}

/** Old states, kept for one wave; each maps to a glyph. */
export type DotState = "online" | "offline" | "running" | "waiting" | "blocked" | "gate" | "done" | "idle"

const dotGlyph: Record<DotState, GlyphState> = {
  online: "live",
  running: "live",
  waiting: "wait",
  offline: "wait",
  idle: "wait",
  // A person's move, not known to be the viewer's: a hollow wait. Callers that know it is the viewer's draw
  // `StatusGlyph state="needs"` (cobalt only for what waits on the viewer, decision 0056 rule 5).
  gate: "wait",
  done: "done",
  blocked: "blocked",
}

const labels: Record<DotState, string> = {
  online: "online",
  offline: "offline",
  running: "running",
  waiting: "waiting",
  blocked: "blocked",
  gate: "needs a person",
  done: "done",
  idle: "idle",
}

/** Status marker for the old states (see `StatusGlyph`). Labels unchanged. */
export function StatusDot({
  state,
  size = 7,
  className,
  label,
  breathe,
}: {
  state: DotState
  size?: 6 | 7 | 8 | 9 | 10 | undefined
  className?: string | undefined
  label?: string | false | undefined
  breathe?: boolean | undefined
}) {
  return (
    <StatusGlyph
      state={dotGlyph[state]}
      size={size}
      breathe={breathe ?? state === "running"}
      className={className}
      label={label === false ? false : (label ?? labels[state])}
    />
  )
}
